import { useState, useEffect } from 'react';
import { Form, Row, Col, Button, InputGroup } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaEye, FaUsers, FaUserShield, FaUserTie, FaUserGraduate, FaEyeSlash, FaDownload, FaFilePdf, FaFutbol } from 'react-icons/fa';
import userService from '../../services/userService';
import jornadaService from '../../services/jornadaService';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import '../../styles/AdminUsers.css';
import '../../styles/admin-mobile-modals.css';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Componente para gestión de usuarios de MongoDB
 * 
 * Permite a administradores:
 * - Ver usuarios con roles: admin, tutor (parent), player, coach
 * - Crear, editar y eliminar usuarios
 * - Activar/desactivar usuarios
 * - Gestionar roles y permisos
 * 
 * Nota: Los futbolistas de PostgreSQL se gestionan en otra página
 */
const UnifiedUserManagement = () => {
  // Obtener usuario logueado
  const { user: currentUser } = useAuth();

  // Estados principales
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view'
  const [selectedUser, setSelectedUser] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'success' });
  const [filterRole, setFilterRole] = useState('all'); // 'all', 'admin', 'parent', 'coach', 'player'
  const [searchTerm, setSearchTerm] = useState('');

  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(20); // 20 usuarios por página

  // Estado para mostrar/ocultar contraseña
  const [showPassword, setShowPassword] = useState(false);

  // Estados para validación de contraseña
  const [passwordValidation, setPasswordValidation] = useState({
    isValid: true,
    message: '',
    strength: 0
  });

  // Effect para manejar el modal en móviles
  useEffect(() => {
    if (showModal) {
      // Forzar scroll al top y prevenir scroll del body
      document.body.style.overflow = 'hidden';
      window.scrollTo(0, 0);
      console.log('📱 Modal abierto - scroll bloqueado');
    } else {
      // Restaurar scroll del body
      document.body.style.overflow = 'unset';
      console.log('📱 Modal cerrado - scroll restaurado');
    }

    // Cleanup al desmontar el componente
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal]);

  // Formulario para usuarios MongoDB
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'parent', // Por defecto parent (tutor)
    lastName: '',
    bio: ''
  });

  useEffect(() => {
    console.log('🚀 Componente montado, currentUser:', currentUser);
    loadData();
  }, []);

  useEffect(() => {
    console.log('👤 CurrentUser cambió:', currentUser);
    if (currentUser) {
      loadData(); // Recargar datos cuando el usuario esté disponible
    }
  }, [currentUser]);

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Cargando usuarios de MongoDB...');
      console.log('👤 Usuario logueado actual:', currentUser);

      // Cargar TODOS los usuarios sin límite de paginación
      const usuariosRes = await userService.getUsers({ limit: 10000 }); // Límite alto para obtener todos
      console.log('📊 Respuesta recibida:', usuariosRes);

      // Extraer usuarios de la respuesta
      const usuariosMongo = usuariosRes.users || usuariosRes.data || [];
      console.log('📋 Usuarios procesados:', usuariosMongo.length);

      // Filtrar usuarios: todos los roles permitidos, pero de admin solo el logueado
      const usuariosFiltrados = usuariosMongo.filter(user => {
        // Incluir todos los usuarios que no sean admin
        if (user.role !== 'admin') {
          return ['parent', 'coach', 'player'].includes(user.role);
        }

        // Para admin, verificar si hay usuario logueado
        if (user.role === 'admin') {
          if (!currentUser) {
            console.warn('⚠️ No hay currentUser disponible');
            return false;
          }

          // Comparaciones para identificar al admin logueado
          const matchById = user._id === currentUser._id;
          const matchByIdAlt = user.id === currentUser.id;
          const matchByEmail = user.email === currentUser.email;
          const matchByEmailAlt = user.email === currentUser.username;

          return matchById || matchByIdAlt || matchByEmail || matchByEmailAlt;
        }
        return false;
      });

      console.log('📋 Usuarios filtrados:', usuariosFiltrados.length);
      console.log('🔍 Detalles de filtrado:', {
        totalFromDB: usuariosMongo.length,
        adminsFromDB: usuariosMongo.filter(u => u.role === 'admin').length,
        currentUserExists: !!currentUser,
        filteredTotal: usuariosFiltrados.length
      });

      setUsuarios(usuariosFiltrados);

      console.log('✅ Datos cargados exitosamente');
      console.log('🔍 Contadores por rol:');
      console.log('- Administradores:', usuariosFiltrados.filter(u => u.role === 'admin').length);
      console.log('- Tutores/Padres:', usuariosFiltrados.filter(u => u.role === 'parent').length);
      console.log('- Entrenadores:', usuariosFiltrados.filter(u => u.role === 'coach').length);
      console.log('- Jugadores:', usuariosFiltrados.filter(u => u.role === 'player').length);

    } catch (error) {
      console.error('❌ Error al cargar datos:', error);
      showAlert('Error al cargar datos: ' + (error.message || error), 'danger');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, variant = 'success') => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: 'success' }), 5000);
  };

  // Función para exportar a Excel
  const exportToExcel = () => {
    try {
      setLoading(true);

      // Obtener usuarios filtrados (respeta filtros aplicados en la UI)
      const filteredUsers = getFilteredUsers();

      // Verificar que hay usuarios para exportar
      if (filteredUsers.length === 0) {
        showAlert('No hay usuarios para exportar con los filtros aplicados', 'warning');
        return;
      }

      // Preparar los datos para Excel
      const excelData = filteredUsers.map(user => ({
        'ID': user._id,
        'Nombre Completo': user.fullName || user.name,
        'Email': user.email,
        'Teléfono': user.phone || '',
        'Rol': user.role === 'admin' ? 'Administrador' :
              user.role === 'coach' ? 'Entrenador' :
              user.role === 'parent' ? 'Padre/Madre' : 'Usuario',
        'Estado': user.status === 'active' ? 'Activo' : 'Inactivo',
        'Perfil Completo': user.profileCompleted ? 'Sí' : 'No',
        'Saldo Wallet': `${user.walletBalance || 0} €`,
        'Nombre del Niño': user.extendedProfile?.childName || user.childName || '',
        'Equipo del Niño': user.extendedProfile?.team || user.team || '',
        'Nombre del Padre/Madre': user.extendedProfile?.parentName || user.parentName || '',
        'Fecha de Registro': user.createdAt ? new Date(user.createdAt).toLocaleDateString('es-ES') : '',
        'Última Actualización': user.updatedAt ? new Date(user.updatedAt).toLocaleDateString('es-ES') : '',
        'Verificado': user.isVerified ? 'Sí' : 'No',
        'Sede Preferida': user.extendedProfile?.preferredVenue || '',
        'Notas': user.extendedProfile?.notes || ''
      }));

      // Crear el libro de Excel
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();

      // Añadir la hoja al libro
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Usuarios');

      // Configurar el ancho de las columnas
      const columnWidths = [
        { wch: 25 }, // ID
        { wch: 25 }, // Nombre Completo
        { wch: 30 }, // Email
        { wch: 15 }, // Teléfono
        { wch: 15 }, // Rol
        { wch: 10 }, // Estado
        { wch: 15 }, // Perfil Completo
        { wch: 15 }, // Saldo Wallet
        { wch: 20 }, // Nombre del Niño
        { wch: 20 }, // Equipo del Niño
        { wch: 25 }, // Nombre del Padre/Madre
        { wch: 15 }, // Fecha de Registro
        { wch: 15 }, // Última Actualización
        { wch: 12 }, // Verificado
        { wch: 20 }, // Sede Preferida
        { wch: 30 }  // Notas
      ];
      worksheet['!cols'] = columnWidths;

      // Generar el nombre del archivo con fecha y hora
      const now = new Date();
      const timestamp = now.toISOString().slice(0, 19).replace(/:/g, '-');
      const filterInfo = filterRole !== 'all' ? `_${filterRole}` : '';
      const searchInfo = searchTerm ? `_busqueda` : '';
      const filename = `usuarios_liga_prokids${filterInfo}${searchInfo}_${timestamp}.xlsx`;

      // Descargar el archivo
      XLSX.writeFile(workbook, filename);

      showAlert(`✅ Excel exportado: ${filteredUsers.length} usuarios`, 'success');

    } catch (error) {
      console.error('Error exportando a Excel:', error);
      showAlert('Error al exportar los datos a Excel', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Función para exportar futbolistas a Excel
  const exportFutbolistasToExcel = async () => {
    try {
      setLoading(true);

      // Obtener datos de futbolistas desde el backend
      const response = await jornadaService.getAllFutbolistasForExport();

      if (!response.success || !response.data || response.data.length === 0) {
        showAlert('No hay futbolistas registrados para exportar', 'warning');
        return;
      }

      const futbolistas = response.data;

      // Preparar los datos para Excel
      const excelData = futbolistas.map(futbolista => ({
        'ID': futbolista.futbolista_id,
        'Nombre': futbolista.nombre || '',
        'Fecha Nacimiento': futbolista.fecha_nacimiento ?
          new Date(futbolista.fecha_nacimiento).toLocaleDateString('es-ES') : '',
        'Edad': futbolista.edad || '',
        'Posición Principal': futbolista.posicion_1 || '',
        'Posición Secundaria': futbolista.posicion_2 || '',
        'Categoría': futbolista.categoria || '',
        'Club': futbolista.club || '',
        'Federación Club': futbolista.club_federacion || '',
        'División': futbolista.division || '',
        'Temporada': futbolista.temporada_nombre || '',
        'Tutor': futbolista.tutor?.nombre || '',
        'Email Tutor': futbolista.tutor?.email || '',
        'Teléfono Tutor': futbolista.tutor?.telefono || '',
        'Fecha Inscripción': futbolista.fecha_inscripcion ?
          new Date(futbolista.fecha_inscripcion).toLocaleDateString('es-ES') : ''
      }));

      // Crear el libro de Excel
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();

      // Añadir la hoja al libro
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Futbolistas');

      // Configurar el ancho de las columnas
      const columnWidths = [
        { wch: 8 },  // ID
        { wch: 25 }, // Nombre
        { wch: 15 }, // Fecha Nacimiento
        { wch: 8 },  // Edad
        { wch: 12 }, // Posición Principal
        { wch: 12 }, // Posición Secundaria
        { wch: 15 }, // Categoría
        { wch: 20 }, // Club
        { wch: 15 }, // Federación Club
        { wch: 15 }, // División
        { wch: 12 }, // Temporada
        { wch: 25 }, // Tutor
        { wch: 30 }, // Email Tutor
        { wch: 15 }, // Teléfono Tutor
        { wch: 15 }  // Fecha Inscripción
      ];
      worksheet['!cols'] = columnWidths;

      // Generar el nombre del archivo con fecha y hora
      const now = new Date();
      const timestamp = now.toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `futbolistas_liga_prokids_${timestamp}.xlsx`;

      // Descargar el archivo
      XLSX.writeFile(workbook, filename);

      showAlert(`✅ Excel exportado: ${futbolistas.length} futbolistas`, 'success');

    } catch (error) {
      console.error('Error exportando futbolistas a Excel:', error);
      showAlert('Error al exportar los futbolistas a Excel', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Función para exportar a PDF
  const exportToPDF = () => {
    try {
      setLoading(true);

      // Obtener usuarios filtrados (respeta filtros aplicados en la UI)
      const filteredUsers = getFilteredUsers();

      // Verificar que hay usuarios para exportar
      if (filteredUsers.length === 0) {
        showAlert('No hay usuarios para exportar con los filtros aplicados', 'warning');
        return;
      }

      // Crear el documento PDF
      const doc = new jsPDF('landscape'); // Orientación horizontal para más columnas

      // Configurar título
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Liga ProKids - Listado de Usuarios', 20, 20);

      // Información de filtros aplicados
      let filterText = '';
      if (filterRole !== 'all') {
        const roleNames = {
          admin: 'Administradores',
          parent: 'Padres/Tutores',
          coach: 'Entrenadores',
          player: 'Jugadores',
          active: 'Usuarios Activos',
          inactive: 'Usuarios Inactivos',
          verified: 'Usuarios Verificados',
          unverified: 'Usuarios No Verificados'
        };
        filterText += `Filtro: ${roleNames[filterRole] || filterRole}`;
      }
      if (searchTerm) {
        filterText += (filterText ? ' | ' : '') + `Búsqueda: "${searchTerm}"`;
      }
      if (filterText) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(filterText, 20, 30);
      }

      // Fecha de generación
      doc.setFontSize(8);
      doc.text(`Generado el: ${new Date().toLocaleString('es-ES')}`, 20, filterText ? 35 : 30);

      // Preparar datos para la tabla
      const tableData = filteredUsers.map(user => [
        user.name || '',
        user.email || '',
        user.phone || '',
        user.role === 'admin' ? 'Admin' :
        user.role === 'coach' ? 'Entrenador' :
        user.role === 'parent' ? 'Padre/Madre' : 'Usuario',
        user.status === 'active' ? 'Activo' : 'Inactivo',
        user.profileCompleted ? 'Sí' : 'No',
        `${user.walletBalance || 0} €`,
        user.extendedProfile?.childName || user.childName || '',
        user.createdAt ? new Date(user.createdAt).toLocaleDateString('es-ES') : ''
      ]);

      // Configurar la tabla
      doc.autoTable({
        head: [['Nombre', 'Email', 'Teléfono', 'Rol', 'Estado', 'Perfil', 'Saldo', 'Niño', 'Registro']],
        body: tableData,
        startY: filterText ? 40 : 35,
        styles: {
          fontSize: 8,
          cellPadding: 2
        },
        headStyles: {
          fillColor: [6, 17, 86], // Color azul de Liga ProKids
          textColor: 255,
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        columnStyles: {
          0: { cellWidth: 30 }, // Nombre
          1: { cellWidth: 40 }, // Email
          2: { cellWidth: 25 }, // Teléfono
          3: { cellWidth: 20 }, // Rol
          4: { cellWidth: 15 }, // Estado
          5: { cellWidth: 15 }, // Perfil
          6: { cellWidth: 15 }, // Saldo
          7: { cellWidth: 25 }, // Niño
          8: { cellWidth: 20 }  // Registro
        }
      });

      // Añadir información de resumen al final
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`Total de usuarios: ${filteredUsers.length}`, 20, finalY);

      // Generar el nombre del archivo
      const now = new Date();
      const timestamp = now.toISOString().slice(0, 19).replace(/:/g, '-');
      const filterInfo = filterRole !== 'all' ? `_${filterRole}` : '';
      const searchInfo = searchTerm ? `_busqueda` : '';
      const filename = `usuarios_liga_prokids${filterInfo}${searchInfo}_${timestamp}.pdf`;

      // Descargar el archivo
      doc.save(filename);

      showAlert(`✅ PDF exportado: ${filteredUsers.length} usuarios`, 'success');

    } catch (error) {
      console.error('Error exportando a PDF:', error);
      showAlert('Error al exportar los datos a PDF', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = (mode, user = null) => {
    console.log('🔍 Abriendo modal:', { mode, user: user?.name || 'nuevo usuario' });
    setModalMode(mode);
    setSelectedUser(user);

    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '', // Siempre vacío por seguridad
        phone: user.phone || '',
        role: user.role || 'parent',
        lastName: user.lastName || '',
        bio: user.bio || ''
      });
    } else {
      setFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        role: 'parent',
        lastName: '',
        bio: ''
      });
    }

    setShowModal(true);
    console.log('✅ Modal state set to true');
  };

  // Función para validar contraseña
  const validatePassword = (password) => {
    if (!password && modalMode === 'edit') {
      // En modo edición, contraseña vacía es válida (no cambiar)
      setPasswordValidation({ isValid: true, message: '', strength: 0 });
      return true;
    }

    if (!password && modalMode === 'create') {
      setPasswordValidation({
        isValid: false,
        message: 'La contraseña es requerida',
        strength: 0
      });
      return false;
    }

    let strength = 0;
    let message = '';
    let isValid = true;

    // Validaciones básicas
    if (password.length < 6) {
      isValid = false;
      message = 'La contraseña debe tener al menos 6 caracteres';
    } else if (password.length >= 6) {
      strength += 1;
      if (password.length >= 8) strength += 1;
      if (/[A-Z]/.test(password)) strength += 1;
      if (/[a-z]/.test(password)) strength += 1;
      if (/[0-9]/.test(password)) strength += 1;
      if (/[^A-Za-z0-9]/.test(password)) strength += 1;

      if (strength < 3) {
        message = 'Contraseña débil. Incluye mayúsculas, minúsculas y números';
      } else if (strength < 5) {
        message = 'Contraseña moderada';
      } else {
        message = 'Contraseña fuerte';
      }
    }

    setPasswordValidation({ isValid, message, strength });
    return isValid;
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setShowPassword(false); // Reset password visibility
    setPasswordValidation({ isValid: true, message: '', strength: 0 }); // Reset validation
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      role: 'parent',
      lastName: '',
      bio: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validar contraseña en tiempo real
    if (name === 'password') {
      validatePassword(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar contraseña antes de enviar
    if (formData.password || modalMode === 'create') {
      const isPasswordValid = validatePassword(formData.password);
      if (!isPasswordValid) {
        showAlert('Por favor, corrige los errores en la contraseña', 'danger');
        return;
      }
    }

    try {
      const userData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        phone: formData.phone,
        lastName: formData.lastName,
        bio: formData.bio
      };

      // Solo incluir password si se está creando o si se proporcionó una nueva
      if (modalMode === 'create' || formData.password) {
        userData.password = formData.password;
      }

      if (modalMode === 'create') {
        await userService.createUser(userData);
        showAlert('Usuario creado exitosamente');
      } else if (modalMode === 'edit') {
        await userService.updateUser(selectedUser._id, userData);
        showAlert('Usuario actualizado exitosamente');
      }

      handleCloseModal();
      loadData();
    } catch (error) {
      showAlert(error.message || 'Error al guardar usuario', 'danger');
      console.error('Error:', error);
    }
  };

  const handleDelete = async (userId) => {
    try {
      const confirmMessage = '⚠️ ATENCIÓN: Esto eliminará PERMANENTEMENTE el usuario y todos sus datos asociados.\n\n¿Estás completamente seguro de que quieres eliminar este usuario?\n\nEsta acción NO se puede deshacer.';

      if (!window.confirm(confirmMessage)) {
        return;
      }

      await userService.permanentDeleteUser(userId);
      showAlert('Usuario eliminado permanentemente', 'success');
      await loadData();
    } catch (error) {
      console.error('Error al eliminar usuario:', error);

      // Mensaje de error más específico
      if (error.message && error.message.includes('dependencias')) {
        showAlert('No se puede eliminar: El usuario tiene datos dependientes. ' + error.message, 'danger');
      } else {
        showAlert(error.message || 'Error al eliminar usuario permanentemente', 'danger');
      }
    }
  };

  // Función para activar/desactivar usuarios
  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === false ? 'active' : 'inactive';
      const action = newStatus === 'active' ? 'activar' : 'desactivar';

      if (!window.confirm(`¿Estás seguro de que quieres ${action} este usuario?`)) {
        return;
      }

      await userService.toggleUserStatus(userId, newStatus);
      showAlert(`Usuario ${action === 'activar' ? 'activado' : 'desactivado'} exitosamente`);
      loadData();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      showAlert(error.message || 'Error al cambiar el estado del usuario', 'danger');
    }
  };

  // Función para silenciar/desilenciar usuario
  const handleToggleSilenciado = async (userId, currentSilenciado) => {
    try {
      const action = currentSilenciado ? 'desilenciar' : 'silenciar';
      if (!window.confirm(`¿Estás seguro de que quieres ${action} este usuario?\n\nSilenciar excluye al usuario de las notificaciones por email y de los exports Excel.`)) {
        return;
      }

      await userService.toggleSilenciado(userId);
      showAlert(`Usuario ${action === 'silenciar' ? 'silenciado' : 'desilenciado'} exitosamente`);
      loadData();
    } catch (error) {
      console.error('Error al cambiar estado silenciado:', error);
      showAlert(error.message || 'Error al cambiar el estado del usuario', 'danger');
    }
  };

  // Función para obtener el nombre del rol en español
  const getRoleDisplayName = (role) => {
    const roleNames = {
      'admin': 'Administrador',
      'parent': 'Tutor/Padre',
      'coach': 'Entrenador',
      'player': 'Jugador'
    };
    return roleNames[role] || role || 'Sin rol';
  };

  // Función para obtener usuarios filtrados
  const getFilteredUsers = () => {
    let filteredUsers = usuarios;

    // Filtrar por estado/tipo
    if (filterRole !== 'all') {
      switch (filterRole) {
        case 'active':
          filteredUsers = filteredUsers.filter(user => user.active !== false);
          break;
        case 'inactive':
          filteredUsers = filteredUsers.filter(user => user.active === false);
          break;
        case 'verified':
          filteredUsers = filteredUsers.filter(user => user.emailVerified === true);
          break;
        case 'unverified':
          filteredUsers = filteredUsers.filter(user => user.emailVerified === false);
          break;
        default:
          // Si es un rol específico (admin, parent, etc.)
          filteredUsers = filteredUsers.filter(user => user.role === filterRole);
          break;
      }
    }

    // Filtrar por término de búsqueda
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filteredUsers = filteredUsers.filter(user =>
        (user.name && user.name.toLowerCase().includes(term)) ||
        (user.email && user.email.toLowerCase().includes(term)) ||
        (user.lastName && user.lastName.toLowerCase().includes(term))
      );
    }

    return filteredUsers;
  };

  // Función para formatear fechas
  const formatDate = (dateString) => {
    if (!dateString) return 'No especificado';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  // Función para manejar cambio de filtro y resetear paginación
  const handleFilterChange = (newFilter) => {
    setFilterRole(newFilter);
    setCurrentPage(1); // Resetear a la primera página
  };

  // Función para manejar cambio de búsqueda
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Resetear a la primera página
  };

  // Función para obtener usuarios paginados
  const getPaginatedUsers = (users) => {
    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    return users.slice(indexOfFirstUser, indexOfLastUser);
  };

  // Función para renderizar paginación moderna y responsive
  const renderPagination = (totalUsers) => {
    const totalPages = Math.ceil(totalUsers / usersPerPage);

    if (totalPages <= 1) return null;

    return (
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-4 gap-3">
        {/* Información de paginación */}
        <div className="text-muted small">
          Mostrando {((currentPage - 1) * usersPerPage) + 1} - {Math.min(currentPage * usersPerPage, totalUsers)} de {totalUsers} usuarios
        </div>

        {/* Controles de paginación */}
        <div className="d-flex align-items-center gap-2">
          {/* Botón Primera Página */}
          <button
            className={`btn btn-outline-secondary btn-sm ${currentPage === 1 ? 'disabled' : ''}`}
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            title="Primera página"
            style={{ minWidth: '40px' }}
          >
            ⟪
          </button>

          {/* Botón Anterior */}
          <button
            className={`btn btn-outline-secondary btn-sm ${currentPage === 1 ? 'disabled' : ''}`}
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            style={{ minWidth: '80px' }}
          >
            <span className="me-1">‹</span>
            <span className="d-none d-sm-inline">Anterior</span>
          </button>

          {/* Páginas en desktop */}
          <div className="d-none d-md-flex align-items-center gap-1">
            {/* Primera página */}
            {currentPage > 2 && (
              <>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setCurrentPage(1)}
                  style={{ minWidth: '40px' }}
                >
                  1
                </button>
                {currentPage > 3 && <span className="text-muted mx-1">...</span>}
              </>
            )}

            {/* Página anterior */}
            {currentPage > 1 && (
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => setCurrentPage(currentPage - 1)}
                style={{ minWidth: '40px' }}
              >
                {currentPage - 1}
              </button>
            )}

            {/* Página actual */}
            <button
              className="btn btn-primary btn-sm"
              style={{ minWidth: '40px' }}
              disabled
            >
              {currentPage}
            </button>

            {/* Página siguiente */}
            {currentPage < totalPages && (
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => setCurrentPage(currentPage + 1)}
                style={{ minWidth: '40px' }}
              >
                {currentPage + 1}
              </button>
            )}

            {/* Última página */}
            {currentPage < totalPages - 1 && (
              <>
                {currentPage < totalPages - 2 && <span className="text-muted mx-1">...</span>}
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setCurrentPage(totalPages)}
                  style={{ minWidth: '40px' }}
                >
                  {totalPages}
                </button>
              </>
            )}
          </div>

          {/* Indicador de página en móvil/tablet */}
          <div className="d-md-none">
            <span className="badge bg-light text-dark px-3 py-2">
              {currentPage} / {totalPages}
            </span>
          </div>

          {/* Botón Siguiente */}
          <button
            className={`btn btn-outline-secondary btn-sm ${currentPage === totalPages ? 'disabled' : ''}`}
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{ minWidth: '80px' }}
          >
            <span className="d-none d-sm-inline">Siguiente</span>
            <span className="ms-1">›</span>
          </button>

          {/* Botón Última Página */}
          <button
            className={`btn btn-outline-secondary btn-sm ${currentPage === totalPages ? 'disabled' : ''}`}
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            title="Última página"
            style={{ minWidth: '40px' }}
          >
            ⟫
          </button>
        </div>
      </div>
    );
  };

  // Función para renderizar usuarios (responsive)
  const renderUserTable = () => {
    const allUsers = getFilteredUsers();
    if (allUsers.length === 0) {
      return (
        <div className="empty-state py-5 text-center">
          <FaUsers size={64} className="text-muted mb-3" />
          <h5 className="text-muted mb-2">No hay usuarios que coincidan con los filtros</h5>
          <p className="text-muted mb-4">Intenta cambiar los filtros o agregar un nuevo usuario</p>
          <Button
            variant="primary"
            onClick={() => handleShowModal('create')}
          >
            <FaPlus className="me-1" /> Agregar Usuario
          </Button>
        </div>
      );
    }

    // Obtener usuarios paginados
    const paginatedUsers = getPaginatedUsers(allUsers);
    const totalUsers = allUsers.length;

    return (
      <div className="users-container">
        {/* Información simple de resultados */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <small className="text-muted">
            <strong>{totalUsers}</strong> usuarios encontrados
          </small>
          {totalUsers > usersPerPage && (
            <small className="text-muted">
              Página {currentPage} de {Math.ceil(totalUsers / usersPerPage)}
            </small>
          )}
        </div>

        {/* Vista de tarjetas responsive */}
        <div className="row">
          {paginatedUsers.map((user, index) => (
            <div key={`user-${user._id}-${index}`} className="col-12 col-md-6 col-lg-4 mb-3">
              <div className="card shadow-sm h-100">
                <div className="card-body p-3 d-flex flex-column">
                {/* Header del usuario */}
                <div className="d-flex align-items-start mb-3">
                  <div className="user-avatar me-3 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '45px', height: '45px', fontSize: '18px', fontWeight: 'bold'}}>
                    {(user.name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center justify-content-between">
                      <h6 className="card-title mb-1 fw-bold">{user.name || 'Sin nombre'}</h6>
                      {/* Indicador de estado */}
                      <div className="d-flex gap-1">
                        {user.silenciado && (
                          <span className="badge bg-warning text-dark small">🔇 Silenciado</span>
                        )}
                        <span className={`badge ${user.active === false ? 'bg-danger' : 'bg-success'} small`}>
                          {user.active === false ? '❌ Inactivo' : '✅ Activo'}
                        </span>
                      </div>
                    </div>
                    <p className="card-text text-muted small mb-1">{user.email || 'Sin email'}</p>
                    <span className="badge bg-secondary small">
                      {getRoleDisplayName(user.role)}
                    </span>
                  </div>
                </div>

                {/* Información del usuario */}
                <div className="mb-3">
                  <div className="small text-muted">
                    <div className="mb-1">
                      <strong>👤 Rol:</strong> {getRoleDisplayName(user.role)}
                    </div>
                    <div className="mb-1">
                      <strong>📅 Creado:</strong> {formatDate(user.createdAt)}
                    </div>
                    {user.phone && (
                      <div className="mb-1">
                        <strong>📞 Teléfono:</strong> {user.phone}
                      </div>
                    )}
                    {user.lastName && (
                      <div className="mb-1">
                        <strong>📝 Apellido:</strong> {user.lastName}
                      </div>
                    )}
                    {user.emailVerified !== undefined && (
                      <div className="mb-1">
                        <strong>✉️ Email verificado:</strong> {user.emailVerified ? '✅ Sí' : '❌ No'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Botones de acción - Solo iconos */}
                <div className="d-flex justify-content-end gap-1 pt-2 border-top mt-auto">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleShowModal('view', user)}
                    className="p-1 d-flex align-items-center justify-content-center"
                    style={{width: '32px', height: '32px'}}
                    title="Ver detalles"
                  >
                    <FaEye size={14} />
                  </Button>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={() => handleShowModal('edit', user)}
                    className="p-1 d-flex align-items-center justify-content-center"
                    style={{width: '32px', height: '32px'}}
                    title="Editar usuario"
                  >
                    <FaEdit size={14} />
                  </Button>

                  {/* Botón Silenciar/Desilenciar - Solo para roles NO jugador */}
                  {user.role !== 'player' && (
                    <Button
                      variant={user.silenciado ? "outline-info" : "outline-secondary"}
                      size="sm"
                      onClick={() => handleToggleSilenciado(user._id, user.silenciado)}
                      className="p-1 d-flex align-items-center justify-content-center"
                      style={{width: '32px', height: '32px'}}
                      title={user.silenciado ? "Desilenciar usuario (volver a incluir en emails y excels)" : "Silenciar usuario (excluir de emails y excels)"}
                    >
                      {user.silenciado ? (
                        <span style={{fontSize: '14px'}}>🔇</span>
                      ) : (
                        <span style={{fontSize: '14px'}}>🔔</span>
                      )}
                    </Button>
                  )}

                  {/* Botón Activar/Desactivar */}
                  <Button
                    variant={user.active === false ? "outline-success" : "outline-secondary"}
                    size="sm"
                    onClick={() => handleToggleStatus(user._id, user.active)}
                    className="p-1 d-flex align-items-center justify-content-center"
                    style={{width: '32px', height: '32px'}}
                    title={user.active === false ? "Activar usuario" : "Desactivar usuario"}
                  >
                    {user.active === false ? (
                      <span style={{fontSize: '14px'}}>✅</span>
                    ) : (
                      <span style={{fontSize: '14px'}}>⏸️</span>
                    )}
                  </Button>

                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDelete(user._id)}
                    className="p-1 d-flex align-items-center justify-content-center"
                    style={{width: '32px', height: '32px'}}
                    title="Eliminar usuario"
                  >
                    <FaTrash size={14} />
                  </Button>
                </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Paginación */}
        {renderPagination(totalUsers)}
      </div>
    );
  };

  // Obtener estadísticas por rol
  const getStatistics = () => {
    const stats = {
      total: usuarios.length,
      admin: usuarios.filter(u => u.role === 'admin').length,
      parent: usuarios.filter(u => u.role === 'parent').length,
      coach: usuarios.filter(u => u.role === 'coach').length,
      player: usuarios.filter(u => u.role === 'player').length,
      active: usuarios.filter(u => u.active !== false).length,
      inactive: usuarios.filter(u => u.active === false).length
    };
    return stats;
  };

  const stats = getStatistics();

  return (
    <AdminLayout>
      <div className="container-fluid">
        {/* Alerta */}
        {alert.show && (
          <div className={`alert alert-${alert.variant} alert-dismissible fade show`} role="alert">
            {alert.message}
            <button
              type="button"
              className="btn-close"
              onClick={() => setAlert({ show: false, message: '', variant: 'success' })}
            ></button>
          </div>
        )}

        {/* Header */}
        <div className="mb-4">
          <h2 className="mb-1">
            <FaUsers className="me-2 text-primary" />
            Gestión de Usuarios
          </h2>
          <p className="text-muted mb-0">
            Administra usuarios: Administrador, Tutores, Entrenadores y Jugadores
          </p>
        </div>

        {/* Estadísticas - Completamente horizontal */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="card">
              <div className="card-body py-2">
                <div style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '10px'
                }}>
                  <div style={{display: 'flex', alignItems: 'center', minWidth: 'fit-content'}}>
                    <FaUsers className="text-primary me-1" size={14} />
                    <span className="fw-bold me-1">{stats.total}</span>
                    <small className="text-muted">Total</small>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', minWidth: 'fit-content'}}>
                    <FaUserShield className="text-danger me-1" size={14} />
                    <span className="fw-bold me-1">{stats.admin}</span>
                    <small className="text-muted">Admins</small>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', minWidth: 'fit-content'}}>
                    <FaUserGraduate className="text-info me-1" size={14} />
                    <span className="fw-bold me-1">{stats.parent}</span>
                    <small className="text-muted">Tutores</small>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', minWidth: 'fit-content'}}>
                    <FaUserTie className="text-warning me-1" size={14} />
                    <span className="fw-bold me-1">{stats.coach}</span>
                    <small className="text-muted">Entrenadores</small>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', minWidth: 'fit-content'}}>
                    <FaUsers className="text-success me-1" size={14} />
                    <span className="fw-bold me-1">{stats.player}</span>
                    <small className="text-muted">Jugadores</small>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', minWidth: 'fit-content'}}>
                    <span className="text-success me-1" style={{fontSize: '14px'}}>✅</span>
                    <span className="fw-bold me-1">{stats.active}</span>
                    <small className="text-muted">Activos</small>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', minWidth: 'fit-content'}}>
                    <span className="text-secondary me-1" style={{fontSize: '14px'}}>❌</span>
                    <span className="fw-bold me-1">{stats.inactive}</span>
                    <small className="text-muted">Inactivos</small>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', minWidth: 'fit-content'}}>
                    <span className="text-primary me-1" style={{fontSize: '14px'}}>📊</span>
                    <span className="fw-bold me-1">{Math.round((stats.active / stats.total) * 100) || 0}%</span>
                    <small className="text-muted">Activos</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros y búsqueda - Compacto debajo de estadísticas */}
        <div className="card mb-4">
          <div className="card-body py-3">
            <div className="row align-items-center">
              <div className="col-lg-3 col-md-4 col-sm-6 mb-2 mb-lg-0">
                <select
                  className="form-select form-select-sm"
                  value={filterRole}
                  onChange={(e) => handleFilterChange(e.target.value)}
                >
                  <option value="all">📊 Todos ({stats.total})</option>
                  <option value="admin">🛡️ Administradores ({stats.admin})</option>
                  <option value="parent">👨‍👩‍👧‍👦 Tutores/Padres ({stats.parent})</option>
                  <option value="coach">🏃‍♂️ Entrenadores ({stats.coach})</option>
                  <option value="player">⚽ Jugadores ({stats.player})</option>
                  <option value="active">✅ Solo activos ({stats.active})</option>
                  <option value="inactive">❌ Solo inactivos ({stats.inactive})</option>
                </select>
              </div>
              <div className="col-lg-6 col-md-5 col-sm-6 mb-2 mb-lg-0">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="🔍 Buscar por nombre, email o apellido..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
              <div className="col-lg-3 col-md-3 col-sm-12">
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="flex-fill"
                    onClick={() => {
                      setFilterRole('all');
                      setSearchTerm('');
                      setCurrentPage(1);
                    }}
                  >
                    🗑️ Limpiar
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    className="flex-fill"
                    onClick={exportToExcel}
                    disabled={loading}
                    title="Exportar usuarios filtrados a Excel"
                  >
                    <FaDownload size={12} className="me-1" />
                    Excel
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    className="flex-fill"
                    onClick={exportToPDF}
                    disabled={loading}
                    title="Exportar usuarios filtrados a PDF"
                  >
                    <FaFilePdf size={12} className="me-1" />
                    PDF
                  </Button>
                  <Button
                    variant="info"
                    size="sm"
                    className="flex-fill"
                    onClick={exportFutbolistasToExcel}
                    disabled={loading}
                    title="Exportar todos los futbolistas registrados a Excel"
                  >
                    <FaFutbol size={12} className="me-1" />
                    Excel Futbolistas
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    className="flex-fill"
                    onClick={() => handleShowModal('create')}
                  >
                    <FaPlus size={12} className="me-1" />
                    Nuevo
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contenido principal */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 text-muted">Cargando usuarios...</p>
          </div>
        ) : (
          renderUserTable()
        )}

        {/* Modal para crear/editar/ver usuario */}
        {showModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 99999,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: window.innerWidth <= 768 ? '0.5rem' : '1rem'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleCloseModal();
              }
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: window.innerWidth <= 768 ? '12px' : '12px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                width: window.innerWidth <= 768 ? 'calc(100vw - 1rem)' : 'min(800px, calc(100vw - 2rem))',
                maxWidth: window.innerWidth <= 768 ? 'calc(100vw - 1rem)' : '800px',
                maxHeight: window.innerWidth <= 768 ? 'calc(100vh - 1rem)' : 'calc(100vh - 2rem)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                position: 'relative',
                zIndex: 99999
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div
                style={{
                  padding: '1rem',
                  borderBottom: '1px solid #dee2e6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: 'white',
                  borderRadius: '12px 12px 0 0',
                  flexShrink: 0
                }}
              >
                <h5 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                  {modalMode === 'create' && (
                    <>
                      <FaPlus className="me-2" />
                      Crear Nuevo Usuario
                    </>
                  )}
                  {modalMode === 'edit' && (
                    <>
                      <FaEdit className="me-2" />
                      Editar Usuario
                    </>
                  )}
                  {modalMode === 'view' && (
                    <>
                      <FaEye className="me-2" />
                      Ver Usuario
                    </>
                  )}
                </h5>
                <button
                  onClick={handleCloseModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px'
                  }}
                >
                  ×
                </button>
              </div>

              {/* Body */}
              <div
                style={{
                  padding: '1rem',
                  overflowY: 'auto',
                  flex: 1,
                  backgroundColor: 'white',
                  WebkitOverflowScrolling: 'touch'
                }}
              >
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nombre *</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      disabled={modalMode === 'view'}
                      placeholder="Nombre del usuario"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Apellido</Form.Label>
                    <Form.Control
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      disabled={modalMode === 'view'}
                      placeholder="Apellido del usuario"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email *</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      disabled={modalMode === 'view'}
                      placeholder="correo@ejemplo.com"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Teléfono</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      disabled={modalMode === 'view'}
                      placeholder="+34 123 456 789"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Rol *</Form.Label>
                    <Form.Select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      required
                      disabled={modalMode === 'view'}
                    >
                      <option value="">Seleccionar rol...</option>
                      <option value="admin">Administrador</option>
                      <option value="parent">Tutor/Padre</option>
                      <option value="coach">Entrenador</option>
                      <option value="player">Jugador</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  {modalMode !== 'view' && (
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Contraseña {modalMode === 'edit' ? '(dejar vacío para no cambiar)' : '*'}
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          required={modalMode === 'create'}
                          disabled={modalMode === 'view'}
                          placeholder="Contraseña segura"
                          className={!passwordValidation.isValid ? 'is-invalid' : ''}
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            border: '2px solid #e9ecef',
                            borderLeft: 'none',
                            borderRadius: '0 8px 8px 0'
                          }}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                      </InputGroup>
                      {formData.password && passwordValidation.message && (
                        <div className={`form-text ${passwordValidation.isValid ? 'text-success' : 'text-danger'}`}>
                          {passwordValidation.message}
                          {passwordValidation.strength > 0 && (
                            <div className="password-strength-bar mt-1">
                              <div
                                className="password-strength-fill"
                                style={{
                                  width: `${(passwordValidation.strength / 6) * 100}%`,
                                  backgroundColor: passwordValidation.strength < 3 ? '#dc3545' :
                                                 passwordValidation.strength < 5 ? '#ffc107' : '#28a745'
                                }}
                              ></div>
                            </div>
                          )}
                        </div>
                      )}
                    </Form.Group>
                  )}
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Biografía/Información adicional</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="bio"
                      value={formData.bio}
                      onChange={handleInputChange}
                      disabled={modalMode === 'view'}
                      placeholder="Información adicional sobre el usuario..."
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
              </div>

              {/* Footer */}
              <div
                style={{
                  padding: '1rem',
                  borderTop: '1px solid #dee2e6',
                  backgroundColor: 'white',
                  borderRadius: '0 0 12px 12px',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '0.5rem',
                  flexShrink: 0
                }}
              >
                <Button
                  variant="secondary"
                  onClick={handleCloseModal}
                  style={{ minHeight: '44px', padding: '0.75rem 1rem' }}
                >
                  {modalMode === 'view' ? 'Cerrar' : 'Cancelar'}
                </Button>
                {modalMode !== 'view' && (
                  <Button
                    variant="primary"
                    onClick={handleSubmit}
                    style={{ minHeight: '44px', padding: '0.75rem 1rem' }}
                  >
                    {modalMode === 'create' ? 'Crear Usuario' : 'Guardar Cambios'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default UnifiedUserManagement;
